import React from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Avatar,
  Chip,
  Button,
} from "@mui/material";
import HomeWorkIcon from "@mui/icons-material/HomeWork";
import PeopleIcon from "@mui/icons-material/People";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import FilterListIcon from "@mui/icons-material/FilterList";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import SearchIcon from "@mui/icons-material/Search";
import VerifiedIcon from "@mui/icons-material/Verified";
import BookmarkAddIcon from "@mui/icons-material/BookmarkAdd";
import ReplayIcon from "@mui/icons-material/Replay";
import SavingsIcon from "@mui/icons-material/Savings";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../../../context/ThemeContext";

const ownerBenefits = [
  {
    icon: <ManageAccountsIcon />,
    title: "Hassle-Free Property Management",
    desc: "Manage all your rental properties from a single dashboard. Track tenant requests, booking status, and payments — all in one place.",
  },
  {
    icon: <CurrencyRupeeIcon />,
    title: "Affordable Fixed Monthly Fee",
    desc: "List and manage each property for just PKR 500 per month. No hidden charges, no commissions — a flat rate that keeps your profits intact.",
  },
  {
    icon: <PeopleAltIcon />,
    title: "Access to Verified Tenants",
    desc: "Receive booking requests from genuine, verified tenants actively looking for rental properties — reducing vacancy time and fraudulent inquiries.",
  },
  {
    icon: <FilterListIcon />,
    title: "Smart Applicant Selection",
    desc: "When multiple tenants apply for your property, you have full control to review all requests and select the most suitable applicant with confidence.",
  },
  {
    icon: <AccountBalanceWalletIcon />,
    title: "Secure Payment Handling",
    desc: "All booking amounts are securely held in our platform wallet until you confirm a tenant — ensuring trust and transparency throughout the process.",
  },
];

const tenantBenefits = [
  {
    icon: <SearchIcon />,
    title: "Find the Right Home Easily",
    desc: "Browse a wide range of verified rental properties across Karachi — filtered by location, price, and size to match your exact requirements.",
  },
  {
    icon: <VerifiedIcon />,
    title: "Verified & Trustworthy Listings",
    desc: "Every property on our platform is listed by a registered owner, ensuring that what you see is accurate, genuine, and up to date.",
  },
  {
    icon: <BookmarkAddIcon />,
    title: "Simple & Fast Booking Process",
    desc: "Submit your booking request in minutes. Our streamlined process eliminates the back-and-forth traditionally involved in renting a home.",
  },
  {
    icon: <ReplayIcon />,
    title: "Guaranteed Refund Policy",
    desc: "If your booking request is not accepted by the owner, your full payment is automatically refunded to your wallet within 3–7 business days — no questions asked.",
  },
  {
    icon: <SavingsIcon />,
    title: "Transparent & Low Commission",
    desc: "Pay only a 5% commission on your confirmed booking. No surprise fees, no inflated prices — just affordable access to quality housing.",
  },
];

const WhyChooseSection = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();

  return (
    <Box id="why-choose" sx={{ py: { xs: 8, md: 12 } }}>
      <Container maxWidth="lg">
        {/* Section Header */}
        <Box sx={{ textAlign: "center", mb: 8 }}>
          <Chip
            label="Why Choose Us"
            sx={{
              bgcolor: theme.lightBg,
              color: theme.primary,
              fontWeight: 600,
              mb: 2,
            }}
          />
          <Typography
            variant="h3"
            fontWeight={700}
            sx={{ mb: 2, color: "#1a1a2e" }}
          >
            The Smart Way to Rent
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ maxWidth: 600, mx: "auto" }}
          >
            OwnerTenantSolution eliminates the traditional hassles of property
            rental by providing a complete digital solution — helping owners
            manage properties effortlessly and tenants find their ideal home
            with full transparency and trust.
          </Typography>
        </Box>

        <Grid container spacing={6}>
          {/* For Property Owners */}
          <Grid item xs={12} md={6}>
            <Paper
              sx={{
                p: 4,
                borderRadius: 3,
                height: "100%",
                border: `2px solid ${theme.lightBg}`,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", mb: 4 }}>
                <Avatar
                  sx={{ bgcolor: theme.primary, width: 56, height: 56, mr: 2 }}
                >
                  <HomeWorkIcon sx={{ fontSize: 28 }} />
                </Avatar>
                <Box>
                  <Typography variant="h5" fontWeight={700}>
                    For Property Owners
                  </Typography>
                  <Chip
                    label="PKR 500 / Month"
                    size="small"
                    sx={{
                      bgcolor: "#e8f5e9",
                      color: "#2e7d32",
                      fontWeight: 600,
                      mt: 0.5,
                    }}
                  />
                </Box>
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                {ownerBenefits.map((benefit, index) => (
                  <Box
                    key={index}
                    sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}
                  >
                    <Box sx={{ color: theme.primary, mt: 0.5 }}>
                      {benefit.icon}
                    </Box>
                    <Box>
                      <Typography variant="subtitle1" fontWeight={600}>
                        {benefit.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {benefit.desc}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>

              <Button
                variant="contained"
                fullWidth
                sx={{
                  mt: 4,
                  py: 1.5,
                  bgcolor: theme.primary,
                  fontWeight: 600,
                  "&:hover": { bgcolor: theme.primaryHover },
                }}
                onClick={() =>
                  navigate("/signup", { state: { type: "Landlord" } })
                }
                endIcon={<ArrowForwardIcon />}
              >
                Start Listing Your Property
              </Button>
            </Paper>
          </Grid>

          {/* For Tenants */}
          <Grid item xs={12} md={6}>
            <Paper
              sx={{
                p: 4,
                borderRadius: 3,
                height: "100%",
                border: "2px solid #eef1fd",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", mb: 4 }}>
                <Avatar
                  sx={{ bgcolor: "#667eea", width: 56, height: 56, mr: 2 }}
                >
                  <PeopleIcon sx={{ fontSize: 28 }} />
                </Avatar>
                <Box>
                  <Typography variant="h5" fontWeight={700}>
                    For Tenants
                  </Typography>
                  <Chip
                    label="100% Verified Listings"
                    size="small"
                    sx={{
                      bgcolor: "#e3f2fd",
                      color: "#1976d2",
                      fontWeight: 600,
                      mt: 0.5,
                    }}
                  />
                </Box>
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                {tenantBenefits.map((benefit, index) => (
                  <Box
                    key={index}
                    sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}
                  >
                    <Box sx={{ color: "#667eea", mt: 0.5 }}>
                      {benefit.icon}
                    </Box>
                    <Box>
                      <Typography variant="subtitle1" fontWeight={600}>
                        {benefit.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {benefit.desc}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>

              <Button
                variant="contained"
                fullWidth
                sx={{
                  mt: 4,
                  py: 1.5,
                  bgcolor: "#667eea",
                  fontWeight: 600,
                  "&:hover": { bgcolor: "#5a6fd6" },
                }}
                onClick={() =>
                  navigate("/signup", { state: { type: "Tenant" } })
                }
                endIcon={<ArrowForwardIcon />}
              >
                Find Your Home Today
              </Button>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default WhyChooseSection;